import { createFileRoute, Link } from "@tanstack/react-router";
import { faqPageJson } from "@/lib/faq-schema";
import { faqQuestionNodes } from "@/lib/section-faqs";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { medicalWebPageScript } from "@/lib/medical-schema";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { canonicalAndAlternates } from "@/lib/hreflang";

const TITLE = "Phentermine & Contrave® in Northern Virginia | Virginia Bariatric";
const DESCRIPTION =
  "Phentermine, Contrave®, orlistat & diethylpropion prescribed by a board-certified bariatric surgeon in Herndon, VA. Proven, affordable non-GLP-1 options.";
const URL = "https://virginiabariatric.com/medical-weight-loss/oral-medications";

const FAQS = [
  {
    q: "Is phentermine safe?",
    a: "For appropriately selected patients under physician supervision, yes — it has been prescribed for over 60 years. It is not safe for patients with heart disease, uncontrolled high blood pressure, hyperthyroidism, or glaucoma, or during pregnancy, which is why we screen carefully, monitor blood pressure, and schedule regular follow-ups rather than simply refilling prescriptions.",
  },
  {
    q: "How much weight can I lose on phentermine or Contrave?",
    a: "In clinical studies, average weight loss is about 5% of body weight — roughly 12–15 pounds for a 250-pound patient — with some patients losing considerably more. Results depend heavily on pairing the medication with nutrition changes, which is why our program includes dietitian support rather than medication alone.",
  },
  {
    q: "Do these medications work as well as Ozempic or Zepbound?",
    a: "On average, no — GLP-1 medications produce roughly two to four times more weight loss in trials. But the older medications cost a fraction as much, are more often covered by insurance, and are sometimes the better medical fit. For many patients they deliver exactly the result they need; for others they're a starting point, and we can escalate if warranted.",
  },
  {
    q: "What’s the difference between phentermine and diethylpropion?",
    a: "They work the same way — both are stimulant-class appetite suppressants — but diethylpropion is generally milder, with gentler effects on sleep, jitteriness, and heart rate. Phentermine is usually the first choice; diethylpropion is our go-to alternative when phentermine isn't well tolerated.",
  },
  {
    q: "Can I just get phentermine from an online weight-loss site?",
    a: "Be careful. Phentermine is a controlled substance, and legitimate prescribing requires a real medical evaluation, blood-pressure monitoring, and follow-up. A prescriber who has never examined you can't safely rule out the cardiovascular conditions that make stimulants dangerous. Our evaluations are thorough, and telehealth follow-ups keep ongoing care convenient.",
  },
  {
    q: "Which weight-loss medication is right for me?",
    a: "It depends on whether your challenge is hunger, cravings, or habits — and on your health history, insurance, and goals. That's a 30-minute conversation, not a quiz. Dr. Fitzer prescribes every FDA-approved weight-loss medication and performs bariatric surgery, so the recommendation you get is the one most likely to work for you.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/oral-medications")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        {
        property: "og:image",
        content:
          "https://virginiabariatric.com/og/medications/og-oral-medications.png",
      },
        {
        property: "og:image:alt",
        content: "Oral weight-loss medications — phentermine, Contrave, orlistat — prescribed at Virginia Bariatric Surgery, Herndon, VA",
      },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        {
        name: "twitter:image",
        content:
          "https://virginiabariatric.com/og/medications/og-oral-medications.png",
      },
        {
        name: "twitter:image:alt",
        content: "Oral weight-loss medications — phentermine, Contrave, orlistat — prescribed at Virginia Bariatric Surgery, Herndon, VA",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: import.meta.env.SSR
      ? canonicalAndAlternates("/medical-weight-loss/oral-medications")
      : [{ rel: "canonical", href: URL }],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/medical-weight-loss/oral-medications"),
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          // Every visible question on the page — the FAQ block plus the
          // question-style section headings — mapped to FAQPage.
          mainEntity: faqQuestionNodes("/medical-weight-loss/oral-medications", FAQS),
        }),
      },
    ] : [],
  }),
  component: OralMedicationsPage,
});

function OralMedicationsPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif">
          Phentermine, Contrave<sup>®</sup> & Other Oral Weight-Loss Medications in Northern Virginia
        </h1>
        <p className="mt-1 text-foreground/70 italic">
          Proven, affordable non-GLP-1 options prescribed by a bariatric surgeon
        </p>
      </header>

      <TelehealthCta />

      <p className="mt-3 text-foreground/80">
        GLP-1 medications grab the most headlines, but they aren’t the best option for every patient.
      </p>



      <p className="mt-3 text-foreground/80">
        There are various reasons why patients seek alternatives to injectable meds. Lack of insurance coverage is fairly common, unfortunately. A small percentage of patients have a medical condition that makes injectables unsafe to take. About 10–15% of patients quit injectables because of the side effects.
      </p>

      <p className="mt-3 text-foreground/80">
        For a substantial number of patients, the injectable meds have little to no weight-loss effect. Remember the bell curve? Statistically, half the population is going to find they are less responsive than average to any given medication.
      </p>

      <p className="mt-3 text-foreground/80">
        The good news is that we have some alternatives. There are several FDA-approved oral medications — phentermine, diethylpropion, Contrave<sup>®</sup>, and orlistat — that have a decades-long track record, much better affordability than the injectables, and can produce significant weight loss when they’re prescribed thoughtfully and paired with real support.
      </p>

      <p className="mt-3 text-foreground/80">
        At Virginia Bariatric Surgery in Herndon, Dr. Matthew Fitzer — a fellowship-trained, board-certified bariatric surgeon — has over two decades of clinical practice prescribing the full range of weight-loss medications. He will help you understand the options that best fit your body, your health history, and your budget. We serve Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn, Chantilly, Manassas, and the greater Washington, D.C. metro area, with{" "}
        <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
          telehealth visits
        </Link>{" "}
        available throughout Virginia, Maryland, and beyond.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Why consider a non-GLP-1 medication?
      </h2>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground/80">
        <li>
          <strong>Cost.</strong> All four medications on this page are available as inexpensive generics (or with manufacturer savings programs) — often under $30–$60 per month, versus hundreds for GLP-1s without coverage.
        </li>
        <li>
          <strong>Coverage.</strong> Many insurance plans that exclude semaglutide (Wegovy<sup>®</sup>) or tirzepatide (Zepbound<sup>®</sup>) still cover — or make affordable — the older oral options.
        </li>
        <li>
          <strong>Different mechanisms.</strong> Cravings-driven eating, portion-size struggles, and dietary-fat absorption are different problems, and different medications target each one.
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        Average weight loss with these medications is more modest than with GLP-1s — typically 3–8% of body weight versus 15–21% — and we’re up-front about that fact. For many patients, that’s exactly enough; for others, these medications are a starting point or a maintenance tool within a{" "}
        <Link to="/our-program" className="text-brand-deep underline hover:no-underline">
          comprehensive program
        </Link>
        .
      </p>
      <p className="mt-3 text-foreground/80">
        Also keep in mind that every patient responds to every medication differently – sometimes very differently. People’s responsiveness to weight loss medication falls somewhere on a bell curve. Being a low-responder to the injectables doesn’t mean you won’t be a high-responder to an alternative medication. Patients are often pleasantly surprised by their results with the older drugs.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Comparing your options at a glance
      </h2>
      <p className="mt-3 text-foreground/80">
        *Average results from clinical trials over 6–12 months, alongside diet and lifestyle changes; individual results vary.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="py-2 px-3 text-left font-semibold">Medication</th>
              <th className="py-2 px-3 text-left font-semibold">How it works</th>
              <th className="py-2 px-3 text-left font-semibold">Typical results*</th>
              <th className="py-2 px-3 text-left font-semibold">Best suited for</th>
            </tr>
          </thead>
          <tbody className="text-foreground/80">
            <tr className="border-b border-border">
              <td className="py-2 px-3 font-medium">
                Phentermine (Adipex-P<sup>®</sup>, Lomaira<sup>®</sup>)
              </td>
              <td className="py-2 px-3">Stimulant-class appetite suppressant</td>
              <td className="py-2 px-3">~5% of body weight</td>
              <td className="py-2 px-3">Strong hunger and portion-size struggles; budget-conscious patients</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 px-3 font-medium">Diethylpropion (formerly Tenuate<sup>®</sup>)</td>
              <td className="py-2 px-3">Milder stimulant-class appetite suppressant</td>
              <td className="py-2 px-3">~3–5% of body weight</td>
              <td className="py-2 px-3">Patients who need a gentler alternative to phentermine</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 px-3 font-medium">
                Contrave<sup>®</sup> (naltrexone / bupropion)
              </td>
              <td className="py-2 px-3">Targets cravings and reward-driven eating in the brain</td>
              <td className="py-2 px-3">~5–6% of body weight</td>
              <td className="py-2 px-3">Emotional eating, cravings, “food noise”; patients also managing low mood or quitting smoking</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 px-3 font-medium">
                Orlistat (Xenical<sup>®</sup>, Alli<sup>®</sup> OTC)
              </td>
              <td className="py-2 px-3">Blocks absorption of ~25–30% of dietary fat in the gut</td>
              <td className="py-2 px-3">~3–5% of body weight</td>
              <td className="py-2 px-3">Patients who can’t take stimulants or brain-acting medications; works locally in the gut</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="mt-6 text-[1.125rem] md:text-[1.35rem] font-serif">
        Phentermine: the most-prescribed weight-loss medication in America
      </h3>
      <p className="mt-3 text-foreground/80">
        Phentermine has been FDA-approved since 1959 and remains the most widely prescribed weight-loss medication in the U.S. — because it works, and it’s inexpensive. It reduces appetite by stimulating the release of norepinephrine in the brain, making it easier to eat smaller portions without feeling deprived. It’s taken as a once-daily tablet or capsule, typically in the morning.
      </p>
      <p className="mt-3 text-foreground/80">
        Phentermine is a controlled substance (Schedule IV) approved for short-term use, and while many physicians extend treatment off-label with close monitoring, it requires an in-person evaluation, blood-pressure checks, and regular follow-up. Common side effects include dry mouth, insomnia, and elevated heart rate or blood pressure — which is why it isn’t appropriate for patients with heart disease, uncontrolled hypertension, hyperthyroidism, glaucoma, or during pregnancy. This is a medication that rewards careful prescribing — exactly the kind of oversight a surgical weight-loss practice provides.
      </p>

      <h3 className="mt-6 text-[1.125rem] md:text-[1.35rem] font-serif">
        Diethylpropion: a gentler stimulant alternative
      </h3>
      <p className="mt-3 text-foreground/80">
        You can think of diethylpropion as phentermine’s lesser-known nephew — an appetite suppressant from the same era and class, generally considered milder in both effect and side effects. The main difference is that it has a much shorter half-life. It lasts about six hours compared to phentermine’s twelve to eighteen.
      </p>
      <p className="mt-3 text-foreground/80">
        For patients who feel over-jittery, wired, or have insomnia on phentermine, diethylpropion often provides appetite control they can live with. It is taken as a 75 mg extended-release tablet once daily, or 25 mg before meals.
      </p>
      <p className="mt-3 text-foreground/80">
        Diethylpropion does share phentermine’s general precautions: Schedule IV controlled substance, short-term approval, and unsuitability for patients with cardiovascular disease or uncontrolled blood pressure. Few practices in Northern Virginia offer it, but we keep it in the toolkit.
      </p>


      <h3 className="mt-6 text-[1.125rem] md:text-[1.35rem] font-serif">
        Contrave<sup>®</sup>: for cravings and emotional eating
      </h3>
      <p className="mt-3 text-foreground/80">
        Contrave combines two long-established medications — naltrexone and bupropion — that together act on the brain’s hunger and reward pathways. Where phentermine blunts hunger, Contrave is often the better fit when the problem is <strong>cravings</strong>: eating in response to stress, boredom, or habit rather than physical hunger. In clinical trials, patients lost on average about 5–6% of body weight over a year, with roughly 1 in 3 losing 10% or more.
      </p>
      <p className="mt-3 text-foreground/80">
        Contrave is not a controlled substance and is taken as tablets twice daily after a four-week ramp-up. It should not be used with opioid medications, in patients with seizure disorders or eating disorders, or with uncontrolled high blood pressure, and it carries bupropion’s standard boxed warning regarding mood changes — another reason physician follow-up matters. A manufacturer savings program keeps monthly costs reasonable for most patients even without insurance coverage.
      </p>

      <h3 className="mt-6 text-[1.125rem] md:text-[1.35rem] font-serif">
        Orlistat: weight loss without brain or stimulant effects
      </h3>
      <p className="mt-3 text-foreground/80">
        Orlistat works completely differently: it stays in your digestive tract and blocks the enzyme that digests fat, so roughly a quarter to a third of the fat you eat passes through unabsorbed. Because it doesn’t enter the bloodstream in meaningful amounts or act on the brain, it’s an option for patients who can’t take stimulants, antidepressant-class medications, or GLP-1s — and it’s the only medication on this page with an over-the-counter version (Alli<sup>®</sup>, at half the prescription dose).
      </p>
      <p className="mt-3 text-foreground/80">
        The trade-off is predictable: eat a high-fat meal and you’ll know it — oily stools and urgency are the main side effects, and they’re also a surprisingly effective teacher of low-fat eating habits. We pair orlistat with dietitian guidance and a daily multivitamin (it can reduce absorption of vitamins A, D, E, and K, taken at a separate time of day).
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How we choose the right medication for you
      </h2>
      <p className="mt-3 text-foreground/80">
        There’s no “best” weight-loss medication — there’s the best one <strong>for you</strong>. At your evaluation, we look at:
      </p>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-foreground/80">
        <li>
          <strong>Your eating pattern</strong> — constant hunger points one direction; evening cravings and stress-eating point another
        </li>
        <li>
          <strong>Your health history</strong> — blood pressure, heart health, medications, mental-health history, and prior weight-loss attempts
        </li>
        <li>
          <strong>Your coverage and budget</strong> — we find the most effective option you can actually sustain, whether that’s a $25 generic or a covered GLP-1
        </li>
        <li>
          <strong>Your goals</strong> — a patient seeking 10 pounds of loss and a patient with 100+ pounds to lose need different conversations, and we’re honest about when{" "}
          <Link to="/services" className="text-brand-deep underline hover:no-underline">
            bariatric surgery
          </Link>{" "}
          or a{" "}
          <Link to="/medical-weight-loss/medications" className="text-brand-deep underline hover:no-underline">
            GLP-1 medication
          </Link>{" "}
          would serve you better
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        Medications can also be sequenced or combined over time — for example, using an oral medication for maintenance after a GLP-1, or as a bridge while insurance authorization for another therapy is pending. Because Dr. Fitzer prescribes everything on this page <strong>and</strong> performs{" "}
        <Link to="/services" className="text-brand-deep underline hover:no-underline">
          weight-loss surgery
        </Link>
        , you get one plan, not a sales pitch.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Cost and insurance
      </h2>
      <p className="mt-3 text-foreground/80">
        This is where the older medications shine. Generic phentermine, diethylpropion, and orlistat typically cost <strong>less than a streaming subscription</strong> each month at Northern Virginia pharmacies — with or without insurance — and Contrave’s savings program keeps it affordable for most patients. No prior-authorization battles, no shortages, no compounding-pharmacy risks. Your visit may also be covered by insurance even when a specific medication isn’t; see our{" "}
        <Link to="/resources/insurance-coverage" className="text-brand-deep underline hover:no-underline">
          insurance coverage guide
        </Link>{" "}
        or call us at{" "}
        <a
          href="tel:+17037099771"
          aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
          className="text-brand-deep underline hover:no-underline"
        >
          (703) 709-9771
        </a>{" "}
        and we’ll help you sort it out before your first appointment.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Oral weight-loss medication FAQs
      </h2>
      <div className="mt-3 space-y-4">
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="font-serif text-brand-mid text-[1.125rem]">{f.q}</h3>
            <p className="mt-1 text-foreground/80">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Next steps: find the medication that fits your life
      </h2>
      <p className="mt-3 text-foreground/80">
        You don’t need to guess your way through weight-loss medications — and you don’t need a GLP-1 budget to get real results.{" "}
        <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
          Request an appointment
        </Link>{" "}
        or call{" "}
        <a
          href="tel:+17037099771"
          aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
          className="text-brand-deep underline hover:no-underline"
        >
          (703) 709-9771
        </a>{" "}
        for an evaluation with Dr. Fitzer. Serving Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn, Chantilly, Manassas, and the greater Washington, D.C. metro area — telehealth available throughout Virginia.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/appointments"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>
    </article>
  );
}
